import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TriangleSentimentRibbon — Round 80 (Feb 2026)
 * Renders a compact community-sentiment overlay strip + optional trend
 * graph for a given ticker, intended to live above (or beside) the
 * Triangle radar. Pulls `/api/stocks/<ticker>/community-sentiment`.
 */
export default function TriangleSentimentRibbon({ ticker, defaultExpanded = false, embedded = false, onNavigateWithOrigin = null }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Round 84e — when this ribbon is rendered INSIDE the Stock Modal,
  // the parent passes `onNavigateWithOrigin` so the "Open feed →" jump
  // encodes the modalOrigin into history state. Back from /community
  // then re-opens the modal at the same ticker. Falls back to plain
  // `navigate` when the ribbon is used standalone (e.g. /triangle/X).
  const goToFeed = () => {
    const target = `/community?tab=feed&ticker=${encodeURIComponent(ticker)}`;
    if (typeof onNavigateWithOrigin === 'function') onNavigateWithOrigin(target);
    else navigate(target);
  };

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/stocks/${ticker}/community-sentiment`, { params: { days: 30 } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) {
    return (
      <div className={`rounded-lg border border-border bg-muted/20 px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground ${embedded ? '' : 'mb-3'}`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading community sentiment…
      </div>
    );
  }

  if (!data || !data.total_posts) {
    return (
      <div
        className={`rounded-lg border border-dashed border-border bg-muted/10 px-3 py-2 flex items-center justify-between gap-2 text-xs ${embedded ? '' : 'mb-3'}`}
        data-testid={`sentiment-ribbon-empty-${ticker}`}
      >
        <span className="text-muted-foreground">
          <MessageCircle className="w-3.5 h-3.5 inline mr-1" />
          No community posts on {ticker} yet — be the first.
        </span>
        <button
          type="button"
          onClick={goToFeed}
          className="text-violet-600 dark:text-violet-400 font-semibold hover:underline"
          data-testid={`sentiment-ribbon-go-${ticker}`}
        >
          Open feed →
        </button>
      </div>
    );
  }

  const overall = data.avg_score > 0.05 ? 'bullish' : data.avg_score < -0.05 ? 'bearish' : 'neutral';
  const overallCfg = {
    bullish: { Icon: TrendingUp, cls: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30', txt: 'Bullish' },
    bearish: { Icon: TrendingDown, cls: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/30', txt: 'Bearish' },
    neutral: { Icon: Minus, cls: 'text-zinc-600 dark:text-zinc-400', bg: 'bg-zinc-100 dark:bg-zinc-800', txt: 'Neutral' },
  }[overall];

  return (
    <div
      className={`rounded-lg border border-border bg-card ${embedded ? '' : 'mb-3'}`}
      data-testid={`sentiment-ribbon-${ticker}`}
    >
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold ${overallCfg.bg} ${overallCfg.cls}`}>
          <overallCfg.Icon className="w-3.5 h-3.5" />
          Community: {overallCfg.txt}
        </div>
        {/* Sentiment bar */}
        <div className="flex-1 min-w-[140px] h-2.5 rounded-full overflow-hidden bg-muted flex">
          <div
            className="bg-emerald-500 transition-all"
            style={{ width: `${data.bullish_pct}%` }}
            title={`Bullish ${data.bullish_pct}%`}
          />
          <div
            className="bg-zinc-300 dark:bg-zinc-600 transition-all"
            style={{ width: `${data.neutral_pct}%` }}
            title={`Neutral ${data.neutral_pct}%`}
          />
          <div
            className="bg-rose-500 transition-all"
            style={{ width: `${data.bearish_pct}%` }}
            title={`Bearish ${data.bearish_pct}%`}
          />
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <span className="text-emerald-600 dark:text-emerald-400 font-bold">{data.bullish_pct}%</span>
          <span className="text-zinc-500">{data.neutral_pct}%</span>
          <span className="text-rose-600 dark:text-rose-400 font-bold">{data.bearish_pct}%</span>
        </div>
        <span className="text-[11px] text-muted-foreground">
          {data.total_posts} posts · 30d
        </span>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-[11px] text-violet-600 dark:text-violet-400 font-semibold hover:underline"
          data-testid={`sentiment-ribbon-toggle-${ticker}`}
        >
          {expanded ? 'Hide trend' : 'Trend ▾'}
        </button>
      </div>
      {expanded && <SentimentTrendChart ticker={ticker} />}
    </div>
  );
}

function SentimentTrendChart({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/stocks/${ticker}/community-sentiment`, { params: { days: 30 } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) {
    return (
      <div className="px-3 py-6 text-xs text-muted-foreground flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading trend…
      </div>
    );
  }
  if (!data || !data.trend || data.trend.length === 0) {
    return (
      <div className="px-3 py-4 text-xs text-muted-foreground text-center">
        No trend data yet.
      </div>
    );
  }

  return (
    <div className="px-3 pb-3 pt-1 border-t border-border" data-testid={`sentiment-trend-${ticker}`}>
      <p className="text-[11px] text-muted-foreground mb-1">
        Daily bullish vs bearish posts — last 30 days
      </p>
      <div style={{ width: '100%', height: 160 }}>
        <ResponsiveContainer>
          <AreaChart data={data.trend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`sentBull-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id={`sentBear-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
            <XAxis dataKey="day" tick={{ fontSize: 9 }} tickFormatter={(d) => (d || '').slice(5)} />
            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ fontSize: 11, padding: 6 }}
              formatter={(v, n) => [v, n === 'bullish' ? '🟢 Bullish' : n === 'bearish' ? '🔴 Bearish' : n]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Area type="monotone" dataKey="bullish" stroke="#10b981" fill={`url(#sentBull-${ticker})`} strokeWidth={2} />
            <Area type="monotone" dataKey="bearish" stroke="#f43f5e" fill={`url(#sentBear-${ticker})`} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
